import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Label } from "@fluentui/react";
import Loader from "../LoadSpinner";
import { useCommonControlsStyle } from "../../theme/styles/CommonControlsStyle";
import {
  DATA_LOAD_STATUS,
} from "../../redux/constants/metaData";
import {
  DOCUMENT_LOGICAL_UNITS_ERROR,
  NO_LOGICALUNIT_FOUND,
} from "../../redux/constants/messageTypes";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useTools } from "../../redux/hooks/useTools";
import { useTheme } from "../../theme/OAS365Theme";

const MENU_PORTAL_Z = 13000;

const DropDownLogicalUnits = ({
  menuPortal = true,
  placeholder = "Select object type...",
}) => {
  const { selectedKeyLuName } = useCheckin();
  const { logicalUnit, objectConnectionDataLoad } = useMetadata();
  const {
    onLogicalUnitListChange,
    onSearchLuNameChange,
    onSelectedKeyLuNameChange,
    onAdvObjMetaDataStatusChanged,
  } = useTools();
  const dataLoadingStatus = {
    completed: "Completed",
    loading: "Loading",
    nodata: "NoData",
    error: "Error",
  };
  const [dataLoad, setDataLoad] = useState(dataLoadingStatus.loading);
  const [optionsData, setOptionsData] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [errorMessage, setErrorMessage] = useState(DOCUMENT_LOGICAL_UNITS_ERROR);

  const theme = useTheme();

  useEffect(() => {
    const hasCachedUnits =
      logicalUnit !== undefined &&
      logicalUnit !== null &&
      logicalUnit.length > 0;
    const isLoading =
      objectConnectionDataLoad === DATA_LOAD_STATUS.onStart ||
      objectConnectionDataLoad === DATA_LOAD_STATUS.onLoad;

    if (hasCachedUnits) {
      const data = getLogicalUnits(logicalUnit);
      setOptionsData(data);
      onLogicalUnitListChange(data);
      setDataLoad(dataLoadingStatus.completed);
      return;
    }

    if (isLoading) {
      setDataLoad(dataLoadingStatus.loading);
      return;
    }

    if (objectConnectionDataLoad === DATA_LOAD_STATUS.error) {
      setDataLoad(dataLoadingStatus.error);
      return;
    }

    setDataLoad(dataLoadingStatus.nodata);
  }, [logicalUnit, objectConnectionDataLoad, onLogicalUnitListChange]);

  useEffect(() => {
    if (selectedOption !== null) {
      onLuNameSelection(selectedOption);
    } else {
      onLuNameSelection({ value: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  function onLuNameSelection(item) {
    onSearchLuNameChange(item.value, item.label, item.viewName);
    onSelectedKeyLuNameChange(item.value);
    onAdvObjMetaDataStatusChanged(DATA_LOAD_STATUS.onStart);
    if (
      item !== undefined &&
      item !== null &&
      item.value !== undefined &&
      item.value !== ""
    ) {
      onAdvObjMetaDataStatusChanged(DATA_LOAD_STATUS.onLoad);
    }
  }

  const styleTheme = useCommonControlsStyle();

  const binIcon = { iconName: "Refresh" };

  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const menuBg = theme.colors.backgroundColor ?? "#fff";
  const borderGrey = theme.colors.Addovation20Grey ?? "#d8d8d8";
  const focusGreen = theme.colors.addovation70Green ?? "#4c8d6a";
  const optionHoverBg =
    theme.colors.msTeamsFilesHover ?? (isDarkMode ? "#343434" : "#EBEBEB");
  const optionSelectedBg = isDarkMode
    ? (theme.colors.dropDownSelectColor ?? "#3A3A3A")
    : (theme.colors.addovation10Grey ??
      theme.colors.Addovation20Grey ??
      "#EEEEEE");
  const scrollbarTrack =
    theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
  const scrollbarThumb =
    theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");
  const scrollbarSize = 10;
  const fontFamily = theme.fonts?.regular ?? "Poppins, sans-serif";
  const fontSize = `${theme.fontSizes?.normal ?? 12}px`;

  useEffect(() => {
    const styleId = "obj-type-select-menu-scrollbar";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      .obj-type-select__menu-list {
        scrollbar-width: thin;
        scrollbar-color: ${scrollbarThumb} ${scrollbarTrack};
      }
      .obj-type-select__menu-list::-webkit-scrollbar {
        width: ${scrollbarSize}px;
      }
      .obj-type-select__menu-list::-webkit-scrollbar-track {
        background: ${scrollbarTrack};
        border-radius: ${scrollbarSize}px;
      }
      .obj-type-select__menu-list::-webkit-scrollbar-thumb {
        background: ${scrollbarThumb};
        border-radius: ${scrollbarSize}px;
        border: 2px solid ${scrollbarTrack};
        min-height: 40px;
      }
      .obj-type-select__menu-list::-webkit-scrollbar-thumb:hover {
        background: ${isDarkMode ? "#9a9a9a" : "#b3b3b3"};
      }
      .obj-type-select__menu-list::-webkit-scrollbar-button {
        display: none;
        height: 0;
        width: 0;
      }
    `;
  }, [scrollbarTrack, scrollbarThumb, scrollbarSize, isDarkMode]);

  const customStyles = {
    control: (base, state) => ({
      ...base,
      fontFamily,
      fontSize,
      minHeight: 38,
      borderRadius: 8,
      backgroundColor: menuBg,
      borderColor: state.isFocused ? focusGreen : borderGrey,
      borderWidth: 1,
      boxShadow: "none",
      "&:hover": {
        borderColor: focusGreen,
      },
    }),
    placeholder: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.deactiveColor ?? "#9e9e9e",
    }),
    singleValue: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.fontColor,
    }),
    input: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.fontColor,
    }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: 10,
      paddingRight: 8,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: 6,
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused
        ? focusGreen
        : (theme.colors.themeDarkColor ??
          theme.colors.addovation70Green ??
          "#4C8D6A"),
    }),
    clearIndicator: (base) => ({
      ...base,
      color: theme.colors.fontColor,
    }),
    menu: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      backgroundColor: menuBg,
      borderRadius: 8,
      marginTop: 6,
      marginBottom: 4,
      boxShadow: isDarkMode
        ? "0 4px 16px rgba(0, 0, 0, 0.45)"
        : "0 4px 16px rgba(0, 0, 0, 0.12)",
      border: `1px solid ${borderGrey}`,
      overflow: "hidden",
      zIndex: MENU_PORTAL_Z,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: MENU_PORTAL_Z,
    }),
    menuList: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      paddingTop: 4,
      paddingBottom: 4,
      maxHeight: 260,
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily,
      fontSize,
      padding: "8px 12px",
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? optionSelectedBg
        : state.isFocused
          ? optionHoverBg
          : menuBg,
      color: theme.colors.fontColor,
      "&:active": {
        backgroundColor: optionHoverBg,
      },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.deactiveColor ?? "#9e9e9e",
    }),
  };

  const loaderBorderStyle = {
    padding: "10px autopx",
    bottom: "50px",
    marginTop: "20px",
  };

  const messageBorderStyle = {
    margin: "auto",
    width: "200px",
    paddingLeft: "0px",
  };

  switch (dataLoad) {
    case dataLoadingStatus.completed:
      return (
        <div style={{ width: "100%" }}>
          <Select
            className="basic-single"
            classNamePrefix="obj-type-select"
            defaultValue={selectedKeyLuName}
            isClearable={true}
            name="logicalUnit"
            options={optionsData}
            placeholder={placeholder}
            styles={customStyles}
            onChange={setSelectedOption}
            maxMenuHeight={260}
            menuPortalTarget={
              menuPortal && typeof document !== "undefined"
                ? document.body
                : null
            }
            menuPosition={menuPortal ? "fixed" : "absolute"}
            menuPlacement="auto"
            menuShouldBlockScroll={false}
            theme={(selectTheme) => ({
              ...selectTheme,
              borderRadius: 8,
              colors: {
                ...selectTheme.colors,
                primary: focusGreen,
                primary25: optionHoverBg,
                neutral0: menuBg,
                neutral80: theme.colors.fontColor,
              },
            })}
          />
        </div>
      );
    case dataLoadingStatus.loading:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div
              style={{
                margin: "auto",
                width: "50px",
                paddingLeft: "0px",
              }}
            >
              <Loader />
            </div>
          </div>
        </>
      );
    case dataLoadingStatus.nodata:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div style={messageBorderStyle}>
              {/* <IconButton
                style={{ marginLeft: "80px" }}
                iconProps={binIcon}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={(ev) => {
                  onObjectConnectionsLoad(teamsContext);
                }}
              /> */}
              <Label style={styleTheme.LabelInformationStyle}>
                {NO_LOGICALUNIT_FOUND}
              </Label>
            </div>
          </div>
        </>
      );
    case dataLoadingStatus.error:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div style={messageBorderStyle}>
              {/* <IconButton
                style={{ marginLeft: "80px" }}
                iconProps={binIcon}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={(ev) => {
                  onObjectConnectionsLoad(teamsContext);
                }}
              /> */}
              <Label style={styleTheme.LabelErrorMessageStyle}>
                {errorMessage}
              </Label>
            </div>
          </div>
        </>
      );
    default:
      return <></>;
  }
};

function getLogicalUnits(logicalUnits) {
  const items = [];
  try {
    if (logicalUnits) {
      logicalUnits.map((item) => {
        items.push({
          value: item.luName,
          label: item.luDesc,
            viewName: item.viewName ?? item.viewname ?? "",
        });
      });
    }
  } catch (ex) {
    console.log(ex.message, "err");
  }

  return items;
}

export default DropDownLogicalUnits;
